/**
 * Vendor Bot Admin - Order Detail Page
 * Single order view with customer info and actions
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useTelegram } from '../../hooks/useTelegram';

const Section = styled(Card)`
  margin-bottom: var(--spacing-md);
`;

const SectionTitle = styled.h3`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: var(--spacing-sm) 0;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: var(--font-sm);
  color: var(--text-secondary);
`;

const InfoValue = styled.span`
  font-size: var(--font-sm);
  color: var(--text-primary);
  font-weight: var(--font-medium);
  text-align: right;
`;

const OrderStatus = styled.div<{ status: string }>`
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-md);
  text-align: center;
  font-size: var(--font-md);
  font-weight: var(--font-semibold);
  margin-bottom: var(--spacing-md);
  background: ${({ status }) => {
    switch (status) {
      case 'completed':
        return 'rgba(76, 175, 80, 0.2)';
      case 'pending':
        return 'rgba(255, 193, 7, 0.2)';
      case 'cancelled':
        return 'rgba(255, 59, 48, 0.2)';
      default:
        return 'rgba(142, 142, 147, 0.2)';
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'completed':
        return 'var(--accent-green)';
      case 'pending':
        return '#FFC107';
      case 'cancelled':
        return 'var(--accent-red)';
      default:
        return 'var(--text-secondary)';
    }
  }};
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--radius-md);
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const ItemName = styled.div`
  font-size: var(--font-md);
  font-weight: var(--font-medium);
  color: var(--text-primary);
`;

const ItemMeta = styled.div`
  font-size: var(--font-sm);
  color: var(--text-secondary);
`;

const ItemPrice = styled.div`
  font-size: var(--font-md);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: var(--spacing-md);
  margin-top: var(--spacing-md);
  border-top: 2px solid var(--border-color);
`;

const TotalLabel = styled.div`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
`;

const TotalAmount = styled.div`
  font-size: var(--font-xl);
  font-weight: var(--font-bold);
  color: var(--accent-green);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: var(--spacing-md);
  margin-top: var(--spacing-md);
`;

const OrderDetail: React.FC = () => {
  const { botUsername, orderId } = useParams<{ botUsername: string; orderId: string }>();
  const navigate = useNavigate();
  const { showBackButton } = useTelegram();
  const [order] = useState({
    orderId: '12345',
    status: 'pending',
    date: 'Oct 6, 2025 10:30 AM',
    customer: {
      name: 'John Doe',
      username: '@johndoe',
      phone: '+1234567890',
    },
    items: [
      { id: 1, name: 'Premium Course', quantity: 1, price: 49.99 },
      { id: 2, name: 'Bonus Material', quantity: 1, price: 0.0 },
    ],
    subtotal: 49.99,
    shipping: 0.0,
    total: 49.99,
    paymentMethod: 'Crypto (BTC)',
    shippingAddress: 'N/A (Digital Product)',
  });

  useEffect(() => {
    showBackButton(() => navigate(`/vendor/${botUsername}/admin/orders`));
  }, [showBackButton, navigate, botUsername]);

  return (
    <Layout header={<Navbar title={`Order #${orderId}`} showBack />}>
      <OrderStatus status={order.status}>
        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
      </OrderStatus>

      <Section>
        <SectionTitle>Order Information</SectionTitle>
        <InfoRow>
          <InfoLabel>Order ID</InfoLabel>
          <InfoValue>#{order.orderId}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Date</InfoLabel>
          <InfoValue>{order.date}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Payment Method</InfoLabel>
          <InfoValue>{order.paymentMethod}</InfoValue>
        </InfoRow>
      </Section>

      <Section>
        <SectionTitle>Customer Information</SectionTitle>
        <InfoRow>
          <InfoLabel>Name</InfoLabel>
          <InfoValue>{order.customer.name}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Username</InfoLabel>
          <InfoValue>{order.customer.username}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Phone</InfoLabel>
          <InfoValue>{order.customer.phone}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Shipping Address</InfoLabel>
          <InfoValue>{order.shippingAddress}</InfoValue>
        </InfoRow>
      </Section>

      <Section>
        <SectionTitle>Order Items</SectionTitle>
        <ItemsList>
          {order.items.map((item) => (
            <Item key={item.id}>
              <ItemInfo>
                <ItemName>{item.name}</ItemName>
                <ItemMeta>Qty: {item.quantity}</ItemMeta>
              </ItemInfo>
              <ItemPrice>${item.price.toFixed(2)}</ItemPrice>
            </Item>
          ))}
        </ItemsList>
        <TotalRow>
          <TotalLabel>Total</TotalLabel>
          <TotalAmount>${order.total.toFixed(2)}</TotalAmount>
        </TotalRow>
      </Section>

      <ActionButtons>
        <Button fullWidth variant="primary">
          Mark as Completed
        </Button>
        <Button fullWidth variant="danger">
          Cancel Order
        </Button>
      </ActionButtons>
    </Layout>
  );
};

export default OrderDetail;
